import React, { useState } from 'react';
import { X, Upload, File as FileIcon, Trash2, Send } from 'lucide-react';

const SubmissionModal = ({
    assignment,
    onSubmit,
    onCancel,
    isSubmitting
}) => {
    const [text, setText] = useState('');
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            setFile(e.dataTransfer.files[0]);
        }
    };

    const handleChange = (e) => {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({
            submissionText: text,
            submissionFile: file
        });
    };

    const canUploadFile = !assignment.submissionType || assignment.submissionType === 'both' || assignment.submissionType === 'file';
    const canEnterText = !assignment.submissionType || assignment.submissionType === 'both' || assignment.submissionType === 'text';

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden animate-fade-in-up">
                <div className="flex justify-between items-center p-5 border-b border-gray-100 bg-gray-50">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">Submit Assignment</h2>
                        <p className="text-sm text-gray-500 mt-0.5">{assignment.title}</p>
                    </div>
                    <button
                        onClick={onCancel}
                        className="text-gray-400 hover:text-gray-600 transition-colors bg-white p-1 rounded-full shadow-sm border border-gray-200"
                    >
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto max-h-[70vh]">

                    {canUploadFile && (
                        <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700">File Upload</label>

                            {!file ? (
                                <div
                                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${dragActive ? 'border-blue-500 bg-blue-50 scale-[1.02]' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                                        }`}
                                    onDragEnter={handleDrag}
                                    onDragLeave={handleDrag}
                                    onDragOver={handleDrag}
                                    onDrop={handleDrop}
                                >
                                    <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-blue-600">
                                        <Upload size={24} />
                                    </div>
                                    <p className="text-gray-900 font-medium mb-1">Drag & Drop your file here</p>
                                    <p className="text-sm text-gray-500 mb-4">or click to browse</p>
                                    <input
                                        type="file"
                                        id="file-upload"
                                        className="hidden"
                                        onChange={handleChange}
                                    />
                                    <label
                                        htmlFor="file-upload"
                                        className="inline-block px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 cursor-pointer transition-colors shadow-sm"
                                    >
                                        Select File
                                    </label>
                                </div>
                            ) : (
                                <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-100 rounded-lg">
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className="bg-blue-100 p-2 rounded-lg text-blue-600 shrink-0">
                                            <FileIcon size={20} />
                                        </div>
                                        <div className="truncate">
                                            <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                                            <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setFile(null)}
                                        className="text-gray-400 hover:text-red-500 p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {canEnterText && (
                        <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700">Text Response</label>
                            <textarea
                                rows="6"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow resize-none"
                                placeholder="Type your answer or comments here..."
                            />
                        </div>
                    )}

                    {!canUploadFile && !canEnterText && (
                        <div className="text-center text-red-500 bg-red-50 p-4 rounded-lg">
                            Configuration Error: No submission type allowed.
                        </div>
                    )}

                </form>

                <div className="p-5 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                    <button
                        onClick={onCancel}
                        className="px-5 py-2.5 text-gray-700 font-medium hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting || (!file && !text && (canUploadFile || canEnterText))}
                        className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none bg-gradient-to-r from-blue-600 to-indigo-600"
                    >
                        {isSubmitting ? 'Submitting...' : (
                            <>
                                <Send size={18} /> Submit Assignment
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SubmissionModal;
